"use client";

import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, GraduationCap } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/global-hooks/useAuth";
import { Toast, ErrorMessages } from "@/lib/utils";
import { getOneTapAuthClient } from "@/lib/auth-client";
import { API_BASE_URL } from "@/lib/api-service";
import { googleSignInErrorMessage } from "@/lib/google-auth-errors";
import { H } from "highlight.run";

/**
 * Fetches the public Google OAuth client ID (not a secret — see
 * be-express/src/modules/public/public.controller.ts). Returns "" on any
 * failure so the caller can treat "not configured" and "failed to load" the
 * same way: render the form with no Google UI, no thrown error, no console
 * noise beyond a warning.
 */
async function fetchGoogleClientId(): Promise<string> {
	try {
		const response = await fetch(`${API_BASE_URL}/public/config`);
		if (!response.ok) return "";
		const body = (await response.json()) as { data?: { googleClientId?: string } };
		return body.data?.googleClientId || "";
	} catch (error) {
		console.warn("Could not load Google Sign-In config:", error);
		return "";
	}
}

export default function LoginForm() {
	const auth = useAuth();
	const location = useLocation();
	const [loading, setLoading] = useState(false);
	const [googleClientId, setGoogleClientId] = useState("");
	const googleButtonRef = useRef<HTMLDivElement>(null);

	// Where email/password login already lands — reused so One Tap goes to the
	// exact same place instead of inventing a second landing rule.
	const landingPath = () => location.state?.from?.pathname || "/dashboard";

	useEffect(() => {
		let cancelled = false;
		fetchGoogleClientId().then((clientId) => {
			if (!cancelled) setGoogleClientId(clientId);
		});
		return () => {
			cancelled = true;
		};
	}, []);

	/**
	 * Renders Google's own "Sign in with Google" button into `googleButtonRef`.
	 *
	 * Passing `button` makes the plugin call `google.accounts.id.initialize()` +
	 * `renderButton()` and never `prompt()`, so the floating One Tap card is gone
	 * and the only Google affordance is a real button sitting in the form. That
	 * matters beyond looks: the prompt is suppressed by browser/ITP state and by
	 * Google's own cooldown after a couple of dismissals, so "nothing happens" is
	 * one of its normal outcomes and is indistinguishable from a broken login. A
	 * rendered button is always there and always fires. Clicking it opens
	 * Google's real account chooser in a popup.
	 *
	 * This is the only Google entry point. The authorization-code redirect
	 * (`signIn.social`) is gone: it needs a client secret the deployment does
	 * not hold, and the server registers no social provider, so calling it
	 * answered `PROVIDER_NOT_FOUND`. One Tap needs no secret — the server checks
	 * the ID token against Google's public keys.
	 *
	 * `callbackURL` leaves the post-sign-in navigation to the plugin, which uses
	 * `window.location`, so the whole app remounts and rebuilds its session store
	 * from the cookie that was just set. Handling it here with react-router does
	 * NOT work: One Tap runs on a SEPARATE client instance (see auth-client.ts),
	 * and a Better Auth session store belongs to the client that created it, so
	 * `authClient` — the one `useSession()` reads — never learns about this
	 * sign-in. Refetching it by hand does not close the gap either. The
	 * destination route's guard then reads "not authenticated" and bounces back
	 * to /auth/login while the cookie is perfectly valid, which looks exactly
	 * like a login that silently failed.
	 *
	 * Note that an `onSuccess` in `fetchOptions` would suppress that redirect, so
	 * only `onError` belongs there.
	 */
	useEffect(() => {
		const container = googleButtonRef.current;
		const oneTapAuth = getOneTapAuthClient(googleClientId);
		if (!container || !oneTapAuth) return;

		// `renderButton` appends; without this a re-render would stack a second
		// button under the first.
		container.replaceChildren();

		oneTapAuth
			.oneTap({
				callbackURL: landingPath(),
				button: {
					container,
					config: {
						type: "standard",
						theme: "outline",
						size: "large",
						text: "signin_with",
						shape: "rectangular",
						locale: "vi",
						// Google caps this at 400 and ignores percentages, so the card's
						// own width has to be measured and passed as a number.
						width: Math.min(container.clientWidth || 320, 400),
					},
				},
				fetchOptions: {
					onError: (ctx) => {
						const message = ctx.error?.message;
						if (message) {
							const { title, detail } = googleSignInErrorMessage(message);
							Toast.error(title, detail);
						}
						console.warn("Google One Tap error:", ctx.error);
					},
				},
			})
			.catch((error: unknown) => {
				const message = error instanceof Error ? error.message : String(error);
				if (message.toLowerCase().includes("net")) {
					Toast.error(ErrorMessages.NETWORK_ERROR, "Vui lòng kiểm tra kết nối của bạn.");
				} else {
					Toast.error(
						"Không hiện được nút Google",
						"Trình duyệt có thể đang chặn Google. Hãy dùng email và mật khẩu.",
					);
				}
				console.error("Google sign-in button failed to render:", error);
			});
		// `landingPath` reads router state that does not change while this page is
		// mounted, so the client id is the only thing worth re-rendering on.
	}, [googleClientId]);

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setLoading(true);

		try {
			const result = await auth.login(e.currentTarget.email.value, e.currentTarget.password.value);

			if (result.error) throw result.error;

			// Không toast thành công — xem ghi chú ở nhánh One Tap phía trên.
			if (result.data?.user) {
				H.identify(result.data.user.id, result.data.user);
			}

			// Send them back to the page they tried to visit when they were
			// redirected to the login page — with a full load, not react-router, and
			// `replace` so the login page does not sit in the history stack waiting
			// for the back button.
			//
			// The full load is what makes the landing page correct. Handing control
			// to the guards inside this same document races the session store: the
			// first render of the destination still sees no role and bounces, so
			// /admin falls through to /dashboard and /dashboard falls through to
			// /statistics. Awaiting `authClient.getSession()` first is not enough
			// either — measured, still /statistics. A fresh document has the cookie
			// and fetches the session before anything renders, which is also exactly
			// what the One Tap branch gets from its `callbackURL`.
			window.location.replace(landingPath());
		} catch (error) {
			// Show appropriate error messages using toast
			const errorMessage = error instanceof Error ? error.message : String(error);

			if (errorMessage && errorMessage.includes("net")) {
				Toast.error(ErrorMessages.NETWORK_ERROR, "Vui lòng kiểm tra kết nối của bạn.");
			} else {
				Toast.error("Email hoặc mật khẩu không đúng", "Vui lòng kiểm tra lại thông tin đăng nhập.");
			}
			console.error("Login failed:", error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Card className="w-full max-w-md">
			<CardHeader className="text-center">
				<div className="flex justify-center mb-4">
					<GraduationCap className="h-12 w-12 text-blue-600 dark:text-blue-400" />
				</div>
				<CardTitle className="text-2xl font-bold">Cổng Thông Tin Giấy Báo Trúng Tuyển</CardTitle>
				<CardDescription>Đăng nhập để truy cập vào hệ thống</CardDescription>
			</CardHeader>
			<CardContent>
				<form onSubmit={handleSubmit} className="space-y-4">
					<div className="space-y-2">
						<label htmlFor="email" className="block text-sm font-medium">
							Email
						</label>
						<Input
							id="email"
							name="email"
							type="email"
							placeholder="admin@gmail.com"
							required
							className="w-full"
							disabled={loading}
						/>
					</div>

					<div className="space-y-2">
						<label htmlFor="password" className="block text-sm font-medium">
							Mật khẩu
						</label>
						<Input
							id="password"
							name="password"
							type="password"
							placeholder="admin"
							required
							className="w-full"
							disabled={loading}
						/>
					</div>

					<Button
						type="submit"
						disabled={loading}
						className="w-full bg-blue-600 hover:bg-blue-700 text-white"
					>
						{loading ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Đang đăng nhập...
							</>
						) : (
							"Đăng nhập"
						)}
					</Button>
				</form>

				{googleClientId && (
					<>
						<div className="relative my-4">
							<div className="absolute inset-0 flex items-center">
								<span className="w-full border-t" />
							</div>
							<div className="relative flex justify-center text-xs uppercase">
								<span className="bg-card px-2 text-muted-foreground">Hoặc</span>
							</div>
						</div>

						{/* Google renders its own button in here. Nothing of ours goes
						    inside: the markup, the wording and the branding are Google's,
						    and its terms require that its button stay unaltered. */}
						<div ref={googleButtonRef} className="flex justify-center" />
					</>
				)}
			</CardContent>
		</Card>
	);
}
